import React, { useState, useEffect, useRef } from "react";
import useGuardedRealtimeList from '../../realtime/useGuardedRealtimeList';
import { Table, Button, Spinner, Alert } from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectApiUrls } from "../../redux/slices/apiUrlSlice";
import axiosInstance from "../../axios/axiosConfig";
import { checkoutRecordUrl, fetchCheckedOutJobs } from '../../services/checkoutService';

const formatDate = value => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toISOString().split('T')[0];
};

function AdminCheckedOut({ refresh }) {
  const { generalProject: endpoint } = useSelector(selectApiUrls);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [revision, setRevision] = useState(0);
  const current = useRef(endpoint);
  current.current = endpoint;
  const generation = useRef(0);
  const savingRef = useRef(false);
  useGuardedRealtimeList({ apiKey: 'generalProject', params: { checkedOut: true },
    load: ({ signal }) => fetchCheckedOutJobs(endpoint, signal),
    apply: rows => { setJobs(rows); setError(null); }, blocked: loading || saving !== null });

  useEffect(() => {
    const controller = new AbortController();
    const id = ++generation.current;
    setJobs([]); setLoading(true); setError(null); setSaving(null); savingRef.current = false;
    const valid = () => !controller.signal.aborted && current.current === endpoint && generation.current === id;
    fetchCheckedOutJobs(endpoint, controller.signal).then(rows => {
      if (valid()) setJobs(rows);
    }).catch(() => {
      if (valid()) setError('Failed to fetch checked-out jobs');
    }).finally(() => { if (valid()) setLoading(false); });
    return () => { controller.abort(); generation.current = id + 1; };
  }, [endpoint, refresh, revision]);

  const handleCheckIn = async jobId => {
    if (savingRef.current || loading) return;
    const id = generation.current;
    const valid = () => current.current === endpoint && generation.current === id;
    savingRef.current = true; setSaving(jobId); setError(null);
    try {
      await axiosInstance.patch(checkoutRecordUrl(endpoint, jobId), {
        ID: jobId, CheckedOutBy: null, CheckedOutDate: null, IsCheckedOut: false,
      });
      if (valid()) setRevision(value => value + 1);
    } catch {
      if (valid()) setError(`Failed to check in job ${jobId}. The checkout has not been confirmed as released.`);
    } finally {
      if (valid()) { setSaving(null); savingRef.current = false; }
    }
  };

  return <div className="m-4 mt-3">
    <h2>Checked Out Jobs</h2>
    {error && <Alert variant="danger">{error}</Alert>}
    {loading ? <Spinner animation="border" role="status"><span className="visually-hidden">Loading checkouts</span></Spinner>
      : jobs.length === 0 ? !error && <Alert variant="info">No checked-out jobs found</Alert>
        : <Table striped bordered hover>
          <thead><tr><th>ID</th><th>Job Number</th><th>Project Name</th><th>Checked Out By</th><th>Checked Out Date</th><th>Action</th></tr></thead>
          <tbody>{jobs.map(job => <tr key={job.ID}>
            <td>{job.ID}</td><td>{job.JobNumber}</td><td>{job.ProjectName}</td>
            <td>{job.CheckedOutBy || 'N/A'}</td><td>{formatDate(job.CheckedOutDate)}</td>
            <td><Button variant="warning" disabled={saving !== null} onClick={() => handleCheckIn(job.ID)}>
              {saving === job.ID ? 'Checking In...' : 'Check In'}
            </Button></td>
          </tr>)}</tbody>
        </Table>}
  </div>;
}

export default AdminCheckedOut;
